import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { Briefcase, MapPin, Clock, Heart, Zap, Coffee, Plane, Monitor, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const benefits = [
  { icon: Monitor, title: "Remote-first", description: "Travaillez d'où vous voulez" },
  { icon: Heart, title: "Santé", description: "Mutuelle premium prise en charge" },
  { icon: Coffee, title: "Équipement", description: "MacBook Pro + budget setup" },
  { icon: Plane, title: "Congés", description: "5 semaines + jours maladie illimités" },
  { icon: Zap, title: "Formation", description: "Budget formation annuel" },
  { icon: Briefcase, title: "Equity", description: "Stock options pour tous" },
];

const positions = [
  {
    title: "Senior Full-Stack Engineer",
    department: "Engineering",
    location: "Remote (France)",
    type: "CDI",
    description: "Rejoignez notre équipe technique pour construire les prochaines fonctionnalités de Floop.",
  },
  {
    title: "ML/AI Engineer",
    department: "AI Research",
    location: "Remote (Europe)",
    type: "CDI",
    description: "Travaillez sur nos modèles IA et améliorez la qualité de génération.",
  },
  {
    title: "Product Designer",
    department: "Design",
    location: "Remote (France)",
    type: "CDI",
    description: "Concevez des expériences utilisateur exceptionnelles pour notre plateforme.",
  },
  {
    title: "Growth Marketing Manager",
    department: "Marketing",
    location: "Paris / Remote",
    type: "CDI",
    description: "Développez notre stratégie d'acquisition et de fidélisation utilisateurs.",
  },
  {
    title: "Customer Success Manager",
    department: "Support",
    location: "Remote (France)",
    type: "CDI",
    description: "Accompagnez nos utilisateurs Pro et Enterprise vers le succès.",
  },
];

const Careers = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero */}
      <section className="pt-24 pb-16 text-center">
        <div className="container mx-auto px-6">
          <span className="inline-block text-sm font-medium text-primary mb-4">
            Carrières
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-display">
            Construisez le futur de la{" "}
            <span className="bg-gradient-floop bg-clip-text text-transparent">
              création IA
            </span>
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Rejoignez une équipe passionnée qui révolutionne la façon dont le monde crée des visuels. 
            Nous cherchons des talents exceptionnels pour nous accompagner dans cette aventure.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
              Pourquoi rejoindre Floop ?
            </h2>
            <p className="text-muted-foreground">
              Des avantages pensés pour votre bien-être et votre épanouissement.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-4 text-center"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <benefit.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-sm mb-1">{benefit.title}</h3>
                <p className="text-xs text-muted-foreground">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4 font-display">
              Postes ouverts
            </h2>
            <p className="text-muted-foreground">
              {positions.length} postes à pourvoir actuellement
            </p>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {positions.map((position, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-2xl p-6 hover:border-primary/50 transition-colors group cursor-pointer"
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded">
                        {position.department}
                      </span>
                      <span className="px-2 py-0.5 bg-secondary text-muted-foreground text-xs font-medium rounded">
                        {position.type}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {position.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-3">{position.description}</p>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {position.location}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" className="rounded-full shrink-0 group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-colors">
                    Postuler
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Spontaneous */}
      <section className="py-16 bg-gradient-floop/10">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Vous ne trouvez pas le poste idéal ?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Envoyez-nous une candidature spontanée. Nous sommes toujours à la recherche de talents.
          </p>
          <Button size="lg" className="bg-gradient-floop hover:opacity-90 text-white rounded-full px-8">
            Candidature spontanée
          </Button>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default Careers;
